<script>
import * as m from '@app/paraglide/messages.js';
import { environment } from "@app/store/envStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { BarLoader } from "svelte-loading-spinners";
/* Firebase */
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";

import { saveAs } from "file-saver";
import Papa from "papaparse";

/* CC LIB */
import tcLib from "@cclib/lib/timecode.js";

// Import the externalized style guide tests
import { validateEventGroup } from "@cclib/lib/styleGuideTests.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";

let styleGuideList = $state([]),
    errors = $state([]),
    userId = environment.online ? firebase.auth().currentUser.uid : null,
    selectedStyleGuide = $state(),
    selectedError = $state(),
    highlight = $state(false),
    gettingRecords = $state();

reloadStyleGuides();

function reloadStyleGuides() {
    styleGuideList = [];
    //Refactor code to use local storage instead of firestore when environment.online is set to false
    if (environment.online) {
        gettingRecords = db
            .collection("users")
            .doc(userId)
            .collection("guides")
            .where("enabled", "==", true)
            .get()
            .then((querySnapshot) => {
                querySnapshot.forEach((doc) => {
                    styleGuideList = [...styleGuideList, doc.data()];
                });

                //Sort style guides by name
                styleGuideList.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });

                selectedStyleGuide =
                    styleGuideList.length > 0 ? styleGuideList[0] : "None";
            })
            .catch((error) => {
                console.log("Error style guides: ", error);
                toast.push(error.message, { classes: ["toast-danger"] });
            });
    } else {
        styleGuideList =
            JSON.parse(localStorage.getItem("cc-style-guides")) || [];
        selectedStyleGuide =
            styleGuideList.length > 0 ? styleGuideList[0] : "None";
    }
}

function runReview() {
    clearErrors();
    // Use the new validateEventGroup function instead of manually running each test
    errors = validateEventGroup(
        eventGroupState[projectState.selected],
        selectedStyleGuide,
        {
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
            excludeSpaces: metricsState.cpsIncludeSpaces === false,
        },
    );

    highlight ? enableHighlighting() : null;

    toast.push(m.style_guide_errors_found_toast({count: errors.length}), { classes: ["toast-info"] });
}

function ignoreError() {
    let errorIndex;
    errors = errors.filter((error, count, allErrors) => {
        if (error.id !== selectedError.id) {
            return true;
        } else {
            errorIndex = count;
        }
    });

    if (errors[errorIndex]) {
        selectedError = errors[errorIndex];
        selectEvent();
    } else {
        selectedError = undefined;
    }

    highlight ? enableHighlighting() : null;
}

function selectAllErrors() {
    eventGroupState[projectState.selected].selected = errors.map((error) => {
        return error.index - 1;
    });
}

function reloadPanel() {
    clearErrors();
    reloadStyleGuides();
}

function clearErrors() {
    selectedError = undefined;
    errors = [];
}

function selectEvent() {
    if (selectedError && selectedError.id) {
        let eventIndex = eventGroupState[
            projectState.selected
        ].events.findIndex((event) => {
            return event.id === selectedError.id;
        });

        eventGroupState[projectState.selected].selected = [eventIndex];
        if (
            lockState.video &&
            eventGroupState[projectState.selected] &&
            eventGroupState[projectState.selected].events[eventIndex] &&
            eventGroupState[projectState.selected].events[eventIndex].start >= 0
        ) {
            player.currentTime =
                eventGroupState[projectState.selected].events[eventIndex].start;
        }

        const selectedIndex =
            eventGroupState[projectState.selected].selected[0];

        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: selectedIndex },
            }),
        );
    }
}

function exportQcReport() {
    toast.push(m.style_guide_exporting_toast(), { classes: ["toast-info"] });

    let csvArray = [];
    errors.forEach((error) => {
        error.messages.forEach((message) => {
            csvArray.push({
                "Style Guide": selectedStyleGuide.name,
                UUID: error.id,
                Index: error.index,
                Start: eventGroupState[projectState.selected].events[
                    error.index - 1
                ]?.start,
                End: eventGroupState[projectState.selected].events[
                    error.index - 1
                ]?.end,
                "Start SMPTE": tcLib.secToTc(
                    eventGroupState[projectState.selected].events[
                        error.index - 1
                    ]?.start,
                    projectState.frameRate,
                    projectState.dropFrame,
                ),
                "End SMPTE": tcLib.secToTc(
                    eventGroupState[projectState.selected].events[
                        error.index - 1
                    ]?.end || 0,
                    projectState.frameRate,
                    projectState.dropFrame,
                ),
                Text: error.text,
                Error: message,
            });
        });
    });

    let csv = Papa.unparse(JSON.stringify(csvArray));

    let fileBlob = new Blob([csv], {
        type: "text/csv;charset=utf-8",
    });

    saveAs(fileBlob, `qc_report_${new Date().toString()}.csv`, {
        autoBom: true,
    });
}

/* TESTS */
</script>

<form id="styleGuideForm" aria-label={m.style_guide_form_aria_label()}>
    {#await gettingRecords}
        <div
            class="d-flex justify-content-center align-items-center p-3"
            role="status"
            aria-label={m.style_guide_loading_aria_label()}>
            <BarLoader size="200" color="#1eb4b2" unit="px" duration="3s"
            ></BarLoader>
        </div>
    {:then}
        <div
            class="btn-group mb-2"
            role="group"
            aria-label={m.style_guide_controls_aria_label()}>
            <div class="btn-group" role="group">
                <select
                    id="styleGuideSelect"
                    class="form-select form-select-sm"
                    bind:value={selectedStyleGuide}
                    onchange={() => clearErrors()}
                    aria-label={m.style_guide_select_aria_label()}>
                    {#each styleGuideList as guide (guide.id)}
                        <option
                            value={guide}
                            selected={selectedStyleGuide.id === guide.id}
                            >{guide.name}</option>
                    {/each}
                    <option selected={!selectedStyleGuide.id}>{m.style_guide_none_option()}</option>
                </select>
            </div>

            <button
                type="button"
                id="startReviewBtn"
                class="btn btn-light {eventGroupState[projectState.selected]
                    ? ''
                    : 'disabled'}"
                aria-label={m.style_guide_start_review_aria_label()}
                onclick={runReview}>
                <i class="bi bi-play-fill" aria-hidden="true"></i> {m.style_guide_start_button()}
            </button>
            <button
                type="button"
                id="ignoreEventBtn"
                class="btn btn-light {selectedError ? '' : 'disabled'}"
                aria-label={m.style_guide_ignore_aria_label()}
                onclick={ignoreError}>
                <i class="bi bi-x" aria-hidden="true"></i> {m.style_guide_ignore_button()}
            </button>
            <button
                type="button"
                id="exportReportBtn"
                onclick={exportQcReport}
                class="btn btn-light {errors.length > 0 ? '' : 'disabled'}"
                aria-label={m.style_guide_export_report_aria_label()}>
                <i class="bi bi-download" aria-hidden="true"></i> {m.style_guide_report_button()}
            </button>

            <button
                type="button"
                id="selectAllErrorsBtn"
                class="btn btn-light {errors.length > 0 ? '' : 'disabled'}"
                aria-label={m.style_guide_select_all_aria_label()}
                onclick={() => {
                    selectAllErrors();
                }}>
                <i class="bi bi-check-all" aria-hidden="true"></i>
            </button>
            <button
                type="button"
                id="resetPanelBtn"
                class="btn btn-light"
                aria-label={m.style_guide_reset_panel_aria_label()}
                onclick={() => reloadPanel()}>
                <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
            </button>
        </div>
        <button
            type="button"
            id="styleGuideManagerBtn"
            class="btn btn-light btn-sm float-end"
            aria-label={m.style_guide_open_manager_aria_label()}
            onclick={() => showModal("styleGuideManager")}>
            <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
        </button>

        {#if selectedStyleGuide.id}
            <div class="row mb-0">
                <div class="col-6">
                    <label class="form-label" for="failedEventsList"
                        >{m.style_guide_failed_events_label()}
                        {#if errors.length > 0}
                            <span
                                class="badge bg-danger align-middle"
                                role="status"
                                aria-label={m.style_guide_errors_count_aria_label({count: errors.length})}
                                >{errors.length}</span>
                        {/if}
                    </label>
                    <select
                        id="failedEventsList"
                        class="form-select form-select-sm resize-vertical"
                        size="6"
                        bind:value={selectedError}
                        onchange={selectEvent}
                        aria-label={m.style_guide_failed_events_aria_label()}>
                        {#each errors as error (error.id)}
                            <option value={error}
                                >{error.index}. {error.text.substring(
                                    0,
                                    42,
                                )}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-6">
                    <label class="form-label" for="errorMessagesList"
                        >{m.style_guide_errors_label()}</label>
                    <select
                        id="errorMessagesList"
                        class="form-select form-select-sm disabled resize-vertical"
                        size="6"
                        aria-label={m.style_guide_error_messages_aria_label()}>
                        {#if selectedError && selectedError.messages}
                            {#each selectedError.messages as message}
                                <option title={message}>{message}</option>
                            {/each}
                        {/if}
                    </select>
                </div>
            </div>
        {:else}
            <p class="text-muted my-5 text-center" role="alert">
                {m.style_guide_select_guide_prompt()}
            </p>
        {/if}
    {/await}
</form>

<style lang="css">
button {
    font-size: 0.8vw;
}
</style>
